import React, { memo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
} from '@mui/material';
import { blue, green, grey } from '@mui/material/colors';
import { getFieldName } from '../../helpers/StateInterpreters';
import { Settings } from '@mui/icons-material';
import { useLangFile } from '../../context/LanguageContext';
import { formatSeason } from '../../helpers/SeasonUtils';

const DuplicateFieldsDialog = (props: DuplicateFieldsDialog.propTypes) => {
  const LangFile = useLangFile();

  const selectedFieldIds = new Set(
    (props.selectedFields || []).map((field) => String(field.fieldId))
  );

  const getSecondaryText = (field) => {
    if (!props.isAddingFields) {
      const fieldSurveys = props.surveys.filter((s) => s.fieldId === field.fieldId);
      if (fieldSurveys.length > 0) {
        return LangFile.DuplicateFieldsDialog.soilSurveys + ': ' + fieldSurveys.length;
      } else {
        return LangFile.DuplicateFieldsDialog.noSoilSurveys;
      }
    } else {
      const fieldSeason = props.seasons.find((season) => season.id === field.seasonId);
      return formatSeason(fieldSeason, LangFile);
    }
  };

  return (
    <Dialog open={true} fullWidth={true} maxWidth={'sm'} onClose={props.onClose}>
      <DialogTitle>{LangFile.DuplicateFieldsDialog.title}</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            mb: 2,
          }}
        >
          {LangFile.DuplicateFieldsDialog.subtitle}
        </Box>

        {!props.isAddingFields && (
          <Box
            sx={{
              bgcolor: grey['300'],
              borderRadius: 16,
              padding: 2,
              fontSize: 12,
              color: grey['800'],
            }}
          >
            <Box>{LangFile.DuplicateFieldsDialog.info1}</Box>
            <Box>{LangFile.DuplicateFieldsDialog.info2}</Box>
          </Box>
        )}

        <Box
          sx={{
            my: 2,
            bgcolor: blue['100'],
            borderRadius: 16,
            padding: 2,
            fontSize: 12,
            color: grey['800'],
          }}
        >
          <Box>
            {props.isBulkSelecting
              ? LangFile.DuplicateFieldsDialog.bulk.info
              : props.isAddingFields
                ? LangFile.DuplicateFieldsDialog.addFields.info
                : LangFile.DuplicateFieldsDialog.info3}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          <List style={{ width: '100%' }}>
            {props.duplicateFields.map((f, idx) => {
              const secondaryText = getSecondaryText(f);
              const isSelected =
                (props.isAddingFields || props.isBulkSelecting) &&
                selectedFieldIds.has(String(f.fieldId));
              return (
                <Paper
                  key={idx}
                  elevation={2}
                  style={{
                    marginTop: 8,
                    border: isSelected ? `1px solid ${green[500]}` : undefined,
                    backgroundColor: isSelected ? green[50] : undefined,
                  }}
                >
                  <ListItem button key={idx} onClick={() => props.onFieldSelect(f)}>
                    <ListItemIcon>
                      <ListItemAvatar>
                        <span style={{ color: isSelected ? green[700] : undefined }}>
                          {idx + 1}
                        </span>
                      </ListItemAvatar>
                    </ListItemIcon>
                    <ListItemText primary={getFieldName(f, LangFile)} secondary={secondaryText} />
                    {!props.isAddingFields && !props.isBulkSelecting && (
                      <ListItemSecondaryAction>
                        <IconButton onClick={() => props.onFieldSettings(f)}>
                          <Settings />
                        </IconButton>
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                </Paper>
              );
            })}
          </List>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>{LangFile.DuplicateFieldsDialog.close}</Button>
      </DialogActions>
    </Dialog>
  );
};

DuplicateFieldsDialog.propTypes = {
  onClose: PropTypes.func,
  onFieldSelect: PropTypes.func,
  onFieldSettings: PropTypes.func,
  surveys: PropTypes.array,
  seasons: PropTypes.array,
  isAddingFields: PropTypes.bool,
  isBulkSelecting: PropTypes.bool,
  selectedFields: PropTypes.array,
};

export default memo(DuplicateFieldsDialog);
